/// <reference types="vite/client" />
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/** Injected by each app's vite.config.ts (`define`). */
declare const __APP_VERSION__: string;

interface VersionProps {
  backendVersion?: string;
  aiServiceVersion?: string;
  /**
   * 'floating' pins the badge to the bottom-right of the viewport (default);
   * 'inline' renders it in flow — e.g. inside the header — with the detail
   * panel dropping below the badge instead of pushing the layout around.
   */
  variant?: 'floating' | 'inline';
  /**
   * Inline only: where the detail panel opens. 'below' (default) hangs it
   * under the badge, centred; 'above' raises it from the badge's start edge,
   * for a badge at the bottom of a sidebar.
   */
  placement?: 'below' | 'above';
}

export function Version({
  backendVersion = '',
  aiServiceVersion = '',
  variant = 'floating',
  placement = 'below',
}: VersionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const versionRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const closeExpanded = () => {
    setIsExpanded(false);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        versionRef.current &&
        !versionRef.current.contains(event.target as Node)
      ) {
        closeExpanded();
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const isInline = variant === 'inline';
  const opensAbove = isInline && placement === 'above';

  const feVersion = __APP_VERSION__ || 'N/A';
  const beVersion = backendVersion ? backendVersion.substring(0, 7) : 'N/A';
  const aiVersion = aiServiceVersion ? aiServiceVersion.substring(0, 7) : 'N/A';

  if (
    !import.meta.env.VITE_SHOW_VERSION ||
    import.meta.env.VITE_SHOW_VERSION === 'false' ||
    localStorage.getItem('showVersion') === undefined
  ) {
    return null;
  }

  return (
    <div
      ref={versionRef}
      style={{
        fontFamily: 'var(--joy-fontFamily-body)',
        ...(isInline
          ? { position: 'relative', display: 'inline-flex' }
          : { position: 'fixed', bottom: '0px', right: '0px', zIndex: 1000 }),
      }}
    >
      {/* Collapsed State - Small Badge */}
      {!isExpanded && (
        <button
          onClick={toggleExpanded}
          style={{
            backgroundColor: '#2c2c2e',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 12px',
            fontSize: '11px',
            fontWeight: '500',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 41, 67, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#001a2e';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 41, 67, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#2c2c2e';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 41, 67, 0.3)';
          }}
        >
          <span>
            {t('v')}
            {feVersion}
          </span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{
              transform: isInline && !opensAbove ? 'rotate(180deg)' : undefined,
            }}
          >
            <path d="M7 14l5-5 5 5z" />
          </svg>
        </button>
      )}

      {/* Expanded State - Detailed Version Info */}
      {isExpanded && (
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            minWidth: '200px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e5e7eb',
            animation:
              isInline && !opensAbove
                ? 'versionSlideDown 0.2s ease-out'
                : 'versionSlideUp 0.2s ease-out',
            /* Inline lives in a fixed-height header, so the panel hangs off the
               badge rather than growing the row it sits in. */
            ...(opensAbove
              ? {
                  position: 'absolute',
                  bottom: 'calc(100% + 8px)',
                  insetInlineStart: 0,
                  zIndex: 1000,
                }
              : isInline
              ? {
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                }
              : {}),
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '700',
                color: '#374151',
              }}
            >
              {t('Version Info')}
            </h3>
            <button
              onClick={closeExpanded}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M19 13H5v-2h14v2z" />
              </svg>
            </button>
          </div>

          {/* Version Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                gap: '16px',
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#64748b',
                  minWidth: 'fit-content',
                }}
              >
                {t('Frontend')}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#1e293b',
                  fontFamily: 'var(--joy-fontFamily-body)',
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: '#e2e8f0',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  marginLeft: 'auto',
                }}
              >
                {t('v')}
                {feVersion}
              </span>
            </div>

            {backendVersion && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '6px',
                  border: '1px solid #dcfce7',
                  gap: '16px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#166534',
                    minWidth: 'fit-content',
                  }}
                >
                  {t('Backend')}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#14532d',
                    fontFamily: 'var(--joy-fontFamily-body)',
                    fontVariantNumeric: 'tabular-nums',
                    backgroundColor: '#dcfce7',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginLeft: 'auto',
                  }}
                >
                  {t('v')}
                  {beVersion}
                </span>
              </div>
            )}

            {aiServiceVersion && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #fde68a',
                  gap: '16px',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#92400e',
                    minWidth: 'fit-content',
                  }}
                >
                  {t('AI Service')}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#78350f',
                    fontFamily: 'var(--joy-fontFamily-body)',
                    fontVariantNumeric: 'tabular-nums',
                    backgroundColor: '#fde68a',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginLeft: 'auto',
                  }}
                >
                  {t('v')}
                  {aiVersion}
                </span>
              </div>
            )}
          </div>

          {/* Build Time (optional) */}
          <div
            style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                color: '#9ca3af',
                textAlign: 'center',
              }}
            >
              {t('Built:')} {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation Keyframes */}
      <style>
        {`
          @keyframes versionSlideUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes versionSlideDown {
            from {
              opacity: 0;
              transform: translate(-50%, -10px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
        `}
      </style>
    </div>
  );
}
